import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdAndCondition,
} from '@bindoc-projects/condition-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { BdAdministrativeDistrictIdCondition } from './administrative-district-id.plugin';
import { BdStateIdsCondition } from './state-id.plugin';
import { BdPostalCodesCondition } from './postal-codes.plugin';
import { BdMarketIdCondition } from './market-id.plugin';

export const FilterConditionMarketContentSelectionPluginType: FilterConditionPluginSpecification<
  {
    marketIds: string[];
    stateIds: number[];
    administrativeDistrictIds: BdAdministrativeDistrictId[];
    postalCodes: string[];
  },
  FilterConditionMarketContentSelectionDto
> = {
  type: 'marketsSelection',
  category: 'person',
  displayName: { de: 'Märkte' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionMarketContentSelectionDto,
  ): FilterConditionMarketContentSelectionPlugin {
    return new FilterConditionMarketContentSelectionPlugin(
      obj.marketIds,
      obj.stateIds,
      obj.administrativeDistrictIds
        ? obj.administrativeDistrictIds.map((it) =>
            BdAdministrativeDistrictId.create(it),
          )
        : [],
      obj.postalCodes,
    );
  },
  fromCondition(): FilterConditionMarketContentSelectionPlugin {
    throw new Error('Not yet implemented');
  },
  newInstance(): FilterConditionMarketContentSelectionPlugin {
    return new FilterConditionMarketContentSelectionPlugin();
  },
};

export class FilterConditionMarketContentSelectionPlugin extends FilterConditionPluginImpl<
  {
    marketIds: string[];
    stateIds: number[];
    administrativeDistrictIds: BdAdministrativeDistrictId[];
    postalCodes: string[];
  },
  FilterConditionMarketContentSelectionDto
> {
  static override Specification: FilterConditionPluginSpecification<
    {
      marketIds: string[];
      stateIds: number[];
      administrativeDistrictIds: BdAdministrativeDistrictId[];
      postalCodes: string[];
    },
    FilterConditionMarketContentSelectionDto
  > = FilterConditionMarketContentSelectionPluginType;

  constructor(
    private marketIds: string[] = [],
    private stateIds: number[] = [],
    private administrativeDistrictIds: BdAdministrativeDistrictId[] = [],
    private postalCodes: string[] = [],
  ) {
    super(FilterConditionMarketContentSelectionPluginType.type);
  }

  clone(): FilterConditionMarketContentSelectionPlugin {
    return new FilterConditionMarketContentSelectionPlugin(
      this.marketIds ? [...this.marketIds] : undefined,
      this.stateIds ? [...this.stateIds] : undefined,
      this.administrativeDistrictIds
        ? [...this.administrativeDistrictIds.map((it) => it.clone())].slice()
        : undefined,
      this.postalCodes ? [...this.postalCodes] : undefined,
    );
  }

  override equals(other: FilterConditionMarketContentSelectionPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.marketIds, other.marketIds) &&
      BdArrayUtil.equals(this.stateIds, other.stateIds) &&
      BdArrayUtil.equals(
        this.administrativeDistrictIds,
        other.administrativeDistrictIds,
        (a, b) => a.value === b.value,
      ) &&
      BdArrayUtil.equals(this.postalCodes, other.postalCodes)
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    return { [ShareableType.MARKET]: this.marketIds };
  }

  /**
   * `toValue()` is an always-truthy object, so the inherited `valid()` accepted
   * a selection without a market — whose `toCondition()` is an empty AND that
   * matches everything (BDA-6550). Mirror {@link validationToText} instead.
   */
  override valid(): boolean {
    return this.marketIds?.length > 0;
  }

  override validationToText(): string {
    if (this.marketIds.length === 0) {
      return 'Mindestens einen Markt auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    const conditions: BdCondition[] = [];

    if (this.marketIds?.length > 0) {
      conditions.push(new BdMarketIdCondition(this.marketIds, []))
    }

    if (this.stateIds?.length > 0) {
      conditions.push(new BdStateIdsCondition(this.stateIds, []));
    }

    if (this.administrativeDistrictIds?.length > 0) {
      conditions.push(
        new BdAdministrativeDistrictIdCondition(
          this.administrativeDistrictIds,
          [],
        ),
      );
    }

    if (this.postalCodes?.length > 0) {
      conditions.push(new BdPostalCodesCondition(this.postalCodes, []));
    }

    if (conditions.length === 1) {
      return conditions[0];
    } else {
      return new BdAndCondition(conditions);
    }
  }

  toJSON(): FilterConditionMarketContentSelectionDto | null {
    return {
      type: this.type,
      marketIds: this.marketIds,
      stateIds: this.stateIds,
      administrativeDistrictIds: this.administrativeDistrictIds.map((it) =>
        it.getNumericValue(),
      ),
      postalCodes: this.postalCodes,
    };
  }

  toText(): string | null {
    let suffix = '';
    if (
      this.stateIds?.length > 0 ||
      this.administrativeDistrictIds?.length > 0 ||
      this.postalCodes?.length > 0
    ) {
      suffix = 'Teile eines Marktes';
    }

    switch (this.marketIds.length) {
      case 0: return suffix;
      case 1: return `ein Markt und ${suffix}`;
      default: return `${this.marketIds.length} Märkte und ${suffix}`;
    }
  }

  toValue(): {
    marketIds: string[];
    stateIds: number[];
    administrativeDistrictIds: BdAdministrativeDistrictId[];
    postalCodes: string[];
  } {
    return {
      administrativeDistrictIds: this.administrativeDistrictIds,
      marketIds: this.marketIds,
      postalCodes: this.postalCodes,
      stateIds: this.stateIds,
    };
  }
}

interface FilterConditionMarketContentSelectionDto {
  type: string;
  marketIds: string[];
  stateIds: number[];
  administrativeDistrictIds: number[];
  postalCodes: string[];
}
